import { useEffect } from 'react'
import { setToken, TOKEN_STORAGE_KEY } from '~/utils/api'

/**
 * Tout changement d'identité passe par un chargement complet de la page : cache de requêtes,
 * contextes, sockets et mémoire de l'onglet repartent de zéro. Aucune purge n'est à maintenir
 * écran par écran, et les données d'un compte ne sont jamais rendues à un autre.
 *
 * Les chemins : la connexion (`signIn`), la déconnexion (`signOut`), un jeton changé ou effacé
 * dans un autre onglet et une page de l'ancienne session restaurée par Retour
 * (`useReloadOnTokenChange`), le 401 du client d'API (`utils/api.ts`). La navigation remplace
 * l'entrée courante de l'historique : Retour ne ramène pas la page d'une autre session.
 */

/**
 * Connexion : jeton posé, puis chargement complet immédiat de la racine, sans rien attendre :
 * l'admin, ses autorisations et sa page d'atterrissage (tablette de pointage comprise) se lisent
 * dans la page rechargée.
 */
export const signIn = async (token: string): Promise<void> => {
  await setToken(token)
  window.location.replace('/')
}

/** Déconnexion : jeton effacé, puis chargement complet de la page de connexion. */
export const signOut = async (): Promise<void> => {
  await setToken(null)
  window.location.replace('/login')
}

/** Un autre onglet a changé ou effacé le jeton (`localStorage.clear()` compris) : on recharge. */
export const reloadOnTokenChange = (
  event: Pick<StorageEvent, 'key' | 'oldValue' | 'newValue'>,
): void => {
  const tokenTouched =
    event.key === null || (event.key === TOKEN_STORAGE_KEY && event.oldValue !== event.newValue)
  if (tokenTouched) window.location.reload()
}

/**
 * - `storage` n'arrive qu'aux AUTRES onglets du navigateur : celui qui se connecte ou se
 *   déconnecte recharge lui-même sa page, les autres suivent par ici ;
 * - `pageshow` persisté : la page sort du cache avant/arrière (Retour après une déconnexion) ; si
 *   le jeton n'est plus celui avec lequel elle a été chargée, elle est rechargée sur-le-champ.
 */
export const useReloadOnTokenChange = (): void => {
  useEffect(() => {
    const loadedWith = window.localStorage.getItem(TOKEN_STORAGE_KEY)
    const reloadIfRestoredStale = (event: PageTransitionEvent) => {
      if (event.persisted && window.localStorage.getItem(TOKEN_STORAGE_KEY) !== loadedWith) {
        window.location.reload()
      }
    }
    window.addEventListener('storage', reloadOnTokenChange)
    window.addEventListener('pageshow', reloadIfRestoredStale)
    return () => {
      window.removeEventListener('storage', reloadOnTokenChange)
      window.removeEventListener('pageshow', reloadIfRestoredStale)
    }
  }, [])
}
